/**
 * Employee Desktop — Bulletins de salaire
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Receipt, Eye, Download, Loader2 } from 'lucide-react';
import config from 'config';

const API = config.API_URL;

export default function EmployeePayslipsDesktopPage() {
  const { token } = useOutletContext();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPayslips = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/employees/me/payslips`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPayslips(data.payslips || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPayslips(); }, [fetchPayslips]);

  const handleAction = async (id, action) => {
    try {
      const res = await fetch(`${API}/api/employees/me/payslips/${id}/${action}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank');
      }
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="desktop-payslips-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-white">Bulletins de salaire</h1>
        <p className="text-white/40 text-sm mt-1">Consultez et téléchargez vos bulletins mensuels</p>
      </div>

      {payslips.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl bg-white/[0.02] border border-white/5">
          <Receipt className="w-14 h-14 text-white/10 mb-4" />
          <p className="text-white/30 text-sm">Aucun bulletin de salaire</p>
          <p className="text-white/20 text-xs mt-1">Vos bulletins seront ajoutés par l'administration</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/5 overflow-hidden" data-testid="desktop-payslips-table">
          <table className="w-full">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Période</th>
                <th className="text-left text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Date d'ajout</th>
                <th className="text-right text-white/30 text-xs font-medium uppercase tracking-wider px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payslips.map(p => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02] transition" data-testid={`desktop-payslip-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#C9A24D]/10 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <span className="text-white text-sm font-semibold">{p.month_label} {p.year}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/40 text-sm">{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleAction(p.id, 'view')}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/40 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 text-xs font-medium transition"
                        data-testid={`desktop-payslip-view-${p.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Voir
                      </button>
                      <button
                        onClick={() => handleAction(p.id, 'download')}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/40 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 text-xs font-medium transition"
                        data-testid={`desktop-payslip-dl-${p.id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Télécharger
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
